import api from './api';
import { API_URL } from '../config';

// Request cache to prevent duplicate calls
const requestCache = new Map<string, Promise<any>>();

interface AuthResponse {
  message?: string;
  user?: any;
  // Backend may still return access_token for backward compatibility, but we won't use it
  access_token?: string;
}

interface UserInfo {
  id: number;
  email: string;
}

// Define getCurrentUser separately to avoid circular reference
const getCurrentUser = async (): Promise<UserInfo> => {
  const cacheKey = 'getCurrentUser';
  
  // Check if there's already a pending request
  if (requestCache.has(cacheKey)) {
    return requestCache.get(cacheKey)!;
  }
  
  // Create new request
  const request = (async () => {
    try {
      const response = await api.get('/users/profile');
      
      // Check if this is an expected 401 response (from interceptor during initial auth check)
      if ((response as any)?.isExpected401) {
        // Return the response object as-is so AuthContext can handle it
        return response as any;
      }
      
      return response.data;
    } catch (error) {
      // Remove from cache on error so we can retry
      requestCache.delete(cacheKey);
      throw error;
    }
  })();
  
  // Cache the request
  requestCache.set(cacheKey, request);
  
  // Clean up cache after request completes (success or failure)
  request.finally(() => {
    requestCache.delete(cacheKey);
  });
  
  return request;
};

export const authService = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    try {
      // Backend will set httpOnly cookie on successful login
      const response = await api.post('/auth/login', {
        email,
        password
      });
      
      // Cookie is set automatically by backend, no need to store anything
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  register: async (email: string, password: string, firstName: string, lastName: string): Promise<AuthResponse> => {
    try {
      // Backend will set httpOnly cookie on successful registration
      const response = await api.post('/auth/register', {
        email,
        password,
        firstName,
        lastName
      });
      
      // Cookie is set automatically by backend, no need to store anything
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  forgotPassword: async (email: string): Promise<void> => {
    try {
      await api.post('/auth/forgot-password', { email });
    } catch (error) {
      throw error;
    }
  },

  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    try {
      await api.post(`/auth/reset-password/${token}`, { newPassword });
    } catch (error) {
      throw error;
    }
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      // Cookie is sent automatically with withCredentials: true
      await api.post('/users/change-password', { 
        currentPassword,
        newPassword 
      });
    } catch (error) {
      throw error;
    }
  },

  logout: async (): Promise<void> => {
    try {
      // Call backend logout endpoint to clear httpOnly cookie
      await api.post('/auth/logout');
    } catch (error) {
      // Even if logout fails, continue - cookie might already be cleared
      console.warn('Logout request failed, but continuing:', error);
    }
  },

  getCurrentUser,

  // Google OAuth functions
  initiateGoogleAuth: () => {
    // Redirect to Google OAuth endpoint
    window.location.href = `${API_URL}/auth/google`;
  },

  handleGoogleCallback: async (code?: string): Promise<AuthResponse> => {
    try {
      if (code) {
        // If code is present, call backend to exchange it for tokens and set cookie
        // Backend will set httpOnly cookie on successful OAuth callback
        const url = `/auth/google/callback?code=${encodeURIComponent(code)}`;
        
        console.log('Calling Google callback endpoint:', url);
        const response = await api.get(url);
        
        // Debug: Log response details
        console.log('Google callback response status:', response.status);
        console.log('Google callback response data:', response.data);
        
        // Note: Set-Cookie headers are not accessible via JavaScript due to security
        // Check browser DevTools Network tab → Response Headers to see Set-Cookie
        // Also check Application/Storage → Cookies to see if cookie was stored
        
        // Wait a moment for cookie to be processed by browser
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Cookie is set automatically by backend, no need to store anything
        // If response includes user data, return it
        if (response.data?.user) {
          console.log('User data received in callback response, returning it');
          return response.data;
        }
        
        // Otherwise, return the response data as-is
        console.log('No user data in response, will fetch from profile endpoint');
        return response.data;
      } else {
        // No code - backend likely already processed the callback server-side
        // Just verify the cookie is set by checking user profile
        // This will fail if cookie isn't set, which is what we want
        const userInfo = await getCurrentUser();
        return { user: userInfo };
      }
    } catch (error: any) {
      console.error('Google callback error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error;
    }
  },
};
